import {
  LayoutDashboard, Users, FolderOpen, Handshake, UserCheck,
  ClipboardList, ShieldCheck, List, UserPlus, KeyRound, Lock,
  LayoutGrid, ArrowLeftRight, Bitcoin, ArrowUpFromLine, Send,
  Percent, BarChart2, TrendingUp, ArrowRightLeft, Wallet,
  ScanFace, MailOpen, Settings2, Landmark, Sliders,
  ArrowDownToLine, CircleDollarSign, Layers, UserCog, Coins,
  FileCheck2, TicketCheck, SlidersHorizontal, WalletCards, ChartCandlestick,BotMessageSquare ,
  CircleUser, History, LifeBuoy, Bot, UserRoundSearch, HardDriveDownload, CloudDownload, BookUser, Newspaper, Share, QrCode,
  Globe, Video, LogIn, Trophy, User, Shield, BookOpen, Key, FileText, FileBarChart, Mail, Bell, Ticket, Settings
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type NavItem = {
  label: string
  icon: LucideIcon | string
  href?: string
  featureSlug?: string
  minAccessLevel?: number
  allowedRoles?: string[]
  children?: NavItem[]
  section?: string
  target?: string
  external?: boolean
  badge?: number | string
}

/**
 * Recursively filters navigation items based on roleSlug and feature permissions.
 * Inherits allowedRoles and featureSlug from parent items when children don't define their own.
 */
export function filterNavItems(
  items: NavItem[],
  hasAccess: (slug: string, minLevel?: number) => boolean,
  roleSlug: string | null,
  parentFeatureSlug?: string,
  parentAllowedRoles?: string[]
): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    // If item has allowedRoles, check if current role is allowed (inheriting from parent if needed)
    const effectiveAllowedRoles = item.allowedRoles || parentAllowedRoles
    if (effectiveAllowedRoles) {
      if (!roleSlug) return acc
      // Check if user has an allowed role OR has L4 feature access (access_level >= 100)
      const hasL4Access =
        item.minAccessLevel &&
        item.minAccessLevel >= 100 &&
        ((item.featureSlug && hasAccess(item.featureSlug, item.minAccessLevel)) ||
         hasAccess('trading_accounts', 100) ||
         hasAccess('settings', 100))
      if (!effectiveAllowedRoles.includes(roleSlug) && !hasL4Access) return acc
    }

    // Effective feature slug: item's own, or inherited from parent
    const effectiveFeatureSlug = item.featureSlug || parentFeatureSlug

    // Filter children first, passing down parent's effective featureSlug and allowedRoles
    const filteredChildren = item.children
      ? filterNavItems(item.children, hasAccess, roleSlug, effectiveFeatureSlug, effectiveAllowedRoles)
      : undefined

    // If item has a featureSlug, check access
    if (effectiveFeatureSlug) {
      const minLevel = item.minAccessLevel ?? 10
      if (!hasAccess(effectiveFeatureSlug, minLevel)) {
        // If parent is denied but has children that pass with different permissions, keep parent wrapper
        if (!filteredChildren || filteredChildren.length === 0) {
          return acc
        }
      }
    }

    // If item has children and ALL children are filtered out, and it has no direct href, hide the parent
    if (item.children && filteredChildren && filteredChildren.length === 0 && !item.href) {
      return acc
    }

    acc.push({
      ...item,
      children: filteredChildren,
    })

    return acc
  }, [])
}

export const adminNavItems: NavItem[] = [
  // TOP PINNED
  { label: 'Dashboard', icon: LayoutDashboard, href: '/admin/overview' },

  // MANAGEMENT
  { label: 'My Incentives', icon: Trophy, href: '/admin/my-incentives', section: 'MANAGEMENT' },
  { label: 'Employees', icon: Users, href: '/admin/users', allowedRoles: ['admin', 'super_admin'], section: 'MANAGEMENT' },
  { label: 'Clients', icon: User, href: '/admin/clients', featureSlug: 'clients', section: 'MANAGEMENT' },

  // TRADING & IB
  { label: 'Trading Streaks', icon: TrendingUp, href: '/admin/streaks', featureSlug: 'streaks', section: 'TRADING & IB' },
  {
    label: 'IB Management', icon: Shield, featureSlug: 'ib_management', section: 'TRADING & IB',
    children: [
      { label: 'IB Requests', icon: ClipboardList, href: '/admin/ib', featureSlug: 'ib_management' },
      { label: 'IB Force Login', icon: LogIn, href: '/admin/ib-forcelogin', featureSlug: 'ib_management' },
      { label: 'IB Questions', icon: Settings2, href: '/admin/ib/questions', featureSlug: 'ib_management' },
      { label: 'IB Assignments', icon: Share, href: '/admin/ib/ibassignments', featureSlug: 'ib_management' },
    ]
  },

  // ADMIN & ACCESS
  {
    label: 'Manage Sub Admins', icon: ShieldCheck, featureSlug: 'manage_subadmins', section: 'ADMIN & ACCESS',
    children: [
      { label: 'Subadmin List', icon: List, href: '/admin/sub-admins/subadminlist', featureSlug: 'manage_subadmins' },
      { label: 'Assignments', icon: BookUser, href: '/admin/sub-admins/assignments', featureSlug: 'manage_subadmins' },
      { label: 'Add Subadmin', icon: UserPlus, href: '/admin/sub-admins/add', featureSlug: 'manage_subadmins' },
      { label: 'Add Subadmin Role', icon: KeyRound, href: '/admin/sub-admins/addsubadminrole', featureSlug: 'admin_roles' },
      { label: 'Role Access', icon: Lock, href: '/admin/sub-admins/roleaccess', featureSlug: 'role_access_change' },
      { label: 'Access Panel', icon: LayoutGrid, href: '/admin/sub-admins/accesspannel', featureSlug: 'manage_access_panel' },
    ]
  },

  // FINANCE
  {
    label: 'Transactions', icon: ArrowLeftRight, featureSlug: 'wallet_transfer', section: 'FINANCE',
    children: [
      { label: 'Deposits', icon: ArrowDownToLine, href: '/admin/deposits', featureSlug: 'deposits' },
      { label: 'Withdrawal', icon: ArrowUpFromLine, href: '/admin/withdraw', featureSlug: 'withdrawals' },
      { label: 'Internal Transfer', icon: ClipboardList, href: '/admin/internal_transfer', featureSlug: 'internal_transfer' },
      { label: 'Wallet Transfers', icon: ArrowRightLeft, href: '/admin/w_2_w_transfer', featureSlug: 'w2w_transfer' },
      { label: 'Online Payments', icon: Send, href: '/admin/payment_gateway', featureSlug: 'wallet_transfe' },
      { label: 'Bonus Setting', icon: Percent, href: '/admin/site-settings/ctrader_bonus', featureSlug: 'ctrader_bonus', minAccessLevel: 100 },
    ]
  },
  { label: 'Ledger Transactions', icon: BookOpen, href: '/admin/ledgertransactions', featureSlug: 'ledger_transactions', section: 'FINANCE' },

  // COMPLIANCE & SECURITY
  {
    label: 'KYC Requests',
    icon: FileText,
    href: '/admin/kyc',
    featureSlug: 'manage_kyc_requests',
    section: 'COMPLIANCE & SECURITY',
    badge: 3
  },
  { label: 'Force Login Audit', icon: Key, href: '/admin/clients/audit', allowedRoles: ['admin', 'super_admin'], section: 'COMPLIANCE & SECURITY' },

  // COMMUNICATION & SYSTEM
  {
    label: 'Reports', icon: FileBarChart, featureSlug: 'reports', section: 'COMMUNICATION & SYSTEM',
    children: [
      { label: 'Deposit Reports', icon: HardDriveDownload, href: '/admin/reports/depositreports', featureSlug: 'reports' },
      { label: 'Withdrawal Reports', icon: CloudDownload, href: '/admin/reports/withdrawreports', featureSlug: 'reports' },
      { label: 'Clients Reports', icon: BookUser, href: '/admin/reports/clientreports', featureSlug: 'reports' },
      { label: 'Internal Reports', icon: ArrowRightLeft, href: '/admin/reports/internalreports', featureSlug: 'reports' },
      { label: 'Wallet Reports', icon: Wallet, href: '/admin/reports/wallet', featureSlug: 'reports' },
    ]
  },
  { label: 'Send Email', icon: Mail, href: '/admin/sendemail', featureSlug: 'send_email', section: 'COMMUNICATION & SYSTEM' },
  { label: 'Push Notifications', icon: Bell, href: '/admin/push_notifications', featureSlug: 'push_notifications', section: 'COMMUNICATION & SYSTEM' },
  { label: 'Tickets', icon: Ticket, href: '/admin/tickets', featureSlug: 'support_tickets', section: 'COMMUNICATION & SYSTEM' },

  // SYSTEM
  {
    label: 'Site Setting', icon: Settings, featureSlug: 'settings', section: 'SYSTEM',
    children: [
      { label: 'Company Bank Details', icon: Landmark, href: '/admin/companybankdetails', featureSlug: 'company_bank_list' },
      { label: 'UPI / QR Management', icon: QrCode, href: '/admin/site-settings/qr', featureSlug: 'settings' },
      { label: 'Leverage Setting', icon: Sliders, href: '/admin/site-settings/leverage_settings', featureSlug: 'leverage_settings' },
      { label: 'Withdraw Setting', icon: ArrowDownToLine, href: '/admin/withdraw_settings', allowedRoles: ['admin', 'super_admin'] },
      { label: 'Currency Setting', icon: CircleDollarSign, href: '/admin/currency_settings', featureSlug: 'currency_settings' },
      { label: 'Account Groups', icon: Layers, href: '/admin/site-settings/account_groups', featureSlug: 'settings' },
      { label: 'Account Types', icon: Layers, href: '/admin/site-settings/account_types' },
      { label: 'Deposit Assets', icon: Coins, href: '/admin/site-settings/depositAssets' },
      { label: 'Account Settings', icon: UserCog, href: '/admin/site-settings/account-settings', featureSlug: 'account_settings' },
      { label: 'Agreements', icon: FileCheck2, href: '/admin/site-settings/agreements', featureSlug: 'agreement_settings' },
      { label: 'Commission & Slabs', icon: Percent, href: '/admin/site-settings/commission', featureSlug: 'settings' },
      { label: 'Deposits Active', icon: Percent, href: '/admin/site-settings/deposits_active', featureSlug: 'settings' },
      {
        label: 'cTrader Leverage & Groups',
        icon: Sliders,
        href: '/admin/site-settings/ctrader_leverage',
        featureSlug: 'trading_accounts',
        minAccessLevel: 100,
        allowedRoles: ['admin', 'super_admin']
      },
    ]
  },
  { label: 'General Setting', icon: SlidersHorizontal, href: '/admin/general_settings', featureSlug: 'general_settings', section: 'SYSTEM' },
  { label: 'Downloads', icon: ArrowDownToLine, href: '/admin/downloads', featureSlug: 'downloads', section: 'SYSTEM' },
  { label: 'Account Management', icon: UserCog, href: '/admin/account_management', featureSlug: 'settings', section: 'SYSTEM' },
]

export const userNavItems: NavItem[] = [
  // OVERVIEW
  { label: 'Dashboard', icon: '/icons/clientsibebar/dashboard.svg', href: '/client/overview', section: 'OVERVIEW' },
  { label: 'Trading Growth', icon: '/icons/clientsibebar/Trading Growth.svg', href: '/client/streaks' },
  { label: 'Web Terminal', icon: ChartCandlestick , href: '/client/web-platform', target: '_blank', external: true },
  { label: 'Downloads', icon: HardDriveDownload, href: '/client/downloads' },
  { label: 'CRM Guide', icon: Video, href: '/client/crm_guide' },
  { label: 'AI Chat Support', icon: BotMessageSquare , href: '/client/ai-chat' },

  // TRADING
  { label: 'My Accounts', icon: '/icons/clientsibebar/My Accounts.svg', href: '/client/accounts', section: 'TRADING' },
  { label: 'Closed Positions', icon: '/icons/clientsibebar/closed position.svg', href: '/client/close_positions' },
  

  // FUNDS
  { label: 'Deposit', icon: '/icons/clientsibebar/Deposit.svg', href: '/client/deposit', section: 'FUNDS' },
  { label: 'Withdrawal', icon: '/icons/clientsibebar/Withdrawal.svg', href: '/client/withdraw' },
  { label: 'Transactions', icon: '/icons/clientsibebar/Transaction.svg', href: '/client/transactions' },
  { label: 'Internal Transfer', icon: '/icons/clientsibebar/Transfer.svg', href: '/client/internal-transfer' },
  { label: 'External Transfer', icon: '/icons/clientsibebar/Transfer.svg', href: '/client/w_2_w_transfer' },
  { label: 'Bank Details', icon: '/icons/clientsibebar/Bank Details.svg', href: '/client/bank' },

  // ACCOUNT
  { label: 'My Profile', icon: '/icons/clientsibebar/My Profile.svg', href: '/client/profile', section: 'ACCOUNT' },
  { label: 'KYC Verification', icon: '/icons/clientsibebar/KYC Verification.svg', href: '/client/kyc' },
  { label: 'IB Portal', icon: '/icons/clientsibebar/IB Portal.svg', href: '/client/ib' },

  // TOOLS
  { label: 'Economic Calendar', icon: '/icons/clientsibebar/Economic Calendar.svg', href: '/client/news', section: 'TOOLS' },
  { label: 'Support Tickets', icon: '/icons/clientsibebar/Support Ticket.svg', href: '/client/tickets' },
  // { label: 'Downloads', icon: HardDriveDownload, href: '/client/downloads' },

  // SETTINGS
  // { label: 'C', icon: '/icons/clientsibebar/Setting.svg', href: '/client/profile' },
]
